import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setLang } from '../utils/langState';
import { LANGS } from '../utils/i18n';
import Sim360Neon from './Sim360Neon';

const LOGO_SRC = process.env.REACT_APP_LOGO_URL;
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL;

const LANGUAGES = [
  {
    code: LANGS.EN,
    native: 'English',
    flag: '🇺🇸',
    accent: '#00f4ff',
    tagline: 'Plan. Model. Learn.',
    cta: 'Enter simulator'
  },
  {
    code: LANGS.ES,
    native: 'Español',
    flag: '🇪🇸',
    accent: '#12b7ff',
    tagline: 'Planea. Modela. Aprende.',
    cta: 'Entrar al simulador'
  },
  {
    code: LANGS.FR,
    native: 'Français',
    flag: '🇫🇷',
    accent: '#7c3aed',
    tagline: 'Planifier. Modéliser. Apprendre.',
    cta: 'Entrer dans le simulateur'
  }
];

const LanguageSelect = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const [logoError, setLogoError] = useState(false);

  const handleSelect = (code) => {
    setLang(code);
    setTimeout(() => navigate(`/?lang=${code}`), 250);
  };

  const activeAccent = hovered
    ? LANGUAGES.find((l) => l.code === hovered)?.accent
    : '#00f4ff';

  return (
    <div
      className="relative min-h-screen overflow-hidden text-white flex flex-col"
      style={{
        backgroundColor: '#020817',
        backgroundImage:
          'radial-gradient(1200px 600px at 10% -10%, rgba(0,244,255,0.08), transparent 60%), radial-gradient(900px 500px at 110% 110%, rgba(124,58,237,0.08), transparent 60%)'
      }}
      data-testid="language-select-page"
    >
      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,244,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,244,255,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 85%)'
        }}
      />

      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-screen"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27180%27 height=%27180%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.6%27/></svg>")'
        }}
      />

      {/* Dynamic accent glow */}
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-700"
        style={{
          background: `radial-gradient(600px 300px at 50% 50%, ${activeAccent}22, transparent 70%)`
        }}
      />

      {/* Top bar: DCA logo + SIM360° neon | Backoffice */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <a
            href="https://thedcaclub.com/en"
            className="flex items-center focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent rounded hover:opacity-80 transition-opacity"
            aria-label="DCA Club"
            data-testid="language-logo"
          >
            {!logoError && LOGO_SRC ? (
              <img
                src={LOGO_SRC}
                alt="DCA Club"
                className="h-7 md:h-8 w-auto"
                onError={() => setLogoError(true)}
                loading="eager"
              />
            ) : (
              <span className="text-[#00f4ff] font-bold text-lg md:text-xl">DCA Club</span>
            )}
          </a>
          <span className="hidden sm:inline-block w-px h-6 bg-white/10" aria-hidden />
          <Sim360Neon size="md" className="hidden sm:inline-flex" />
        </div>

        <a
          href="/myportafolio"
          className="inline-flex items-center rounded-full border border-[#12b7ff] text-[#00f4ff] hover:bg-[rgba(0,244,255,0.08)] px-4 py-2 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-[#00f4ff] focus:ring-offset-2 focus:ring-offset-transparent"
          data-testid="language-backoffice-btn"
        >
          Backoffice
        </a>
      </header>

      {/* Main */}
      <main className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-12 pb-16">
        <div className="max-w-6xl mx-auto w-full">
          <div className="mb-10 md:mb-14">
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] uppercase tracking-[0.3em]"
              style={{ borderColor: `${activeAccent}55`, color: activeAccent }}
              data-testid="language-eyebrow"
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: activeAccent, boxShadow: `0 0 8px ${activeAccent}` }}
              />
              Choose your language
            </div>
            <h1
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight"
              style={{
                background: `linear-gradient(90deg, #ffffff 0%, ${activeAccent} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
              data-testid="language-title"
            >
              Pick the language you think in.
            </h1>
            <p className="mt-4 text-base md:text-lg text-[#9EB4C0] max-w-2xl">
              Simulator 360° will instantly adapt its interface, tooltips and exports.
            </p>
          </div>

          {/* Language cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
            {LANGUAGES.map((lang) => {
              const isHovered = hovered === lang.code;
              return (
                <button
                  key={lang.code}
                  onMouseEnter={() => setHovered(lang.code)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(lang.code)}
                  onBlur={() => setHovered(null)}
                  onClick={() => handleSelect(lang.code)}
                  data-testid={`language-card-${lang.code}`}
                  className="group relative text-left rounded-2xl p-7 md:p-8 overflow-hidden focus:outline-none transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    backgroundColor: 'rgba(11,28,56,0.55)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    border: `1px solid ${isHovered ? lang.accent : 'rgba(255,255,255,0.08)'}`,
                    boxShadow: isHovered
                      ? `0 20px 60px -20px ${lang.accent}66, inset 0 0 0 1px ${lang.accent}33`
                      : '0 10px 30px -20px rgba(0,0,0,0.6)'
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      background: `radial-gradient(320px 180px at 0% 0%, ${lang.accent}22, transparent 60%)`
                    }}
                  />

                  <div className="relative flex items-start justify-between">
                    <div
                      className="text-5xl md:text-6xl leading-none drop-shadow-[0_0_25px_rgba(0,0,0,0.4)] transition-transform duration-500 group-hover:scale-105"
                      aria-hidden
                    >
                      {lang.flag}
                    </div>
                    <div
                      className="text-[10px] tracking-[0.3em] uppercase px-2 py-1 rounded-full border"
                      style={{
                        color: lang.accent,
                        borderColor: `${lang.accent}66`
                      }}
                    >
                      {lang.code}
                    </div>
                  </div>

                  <div className="relative mt-8 md:mt-10">
                    <div className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                      {lang.native}
                    </div>
                    <div className="mt-2 text-sm text-[#9EB4C0]">
                      {lang.tagline}
                    </div>
                  </div>

                  <div className="relative mt-8 flex items-center justify-between">
                    <div
                      className="h-px flex-1 mr-4 transition-all duration-500"
                      style={{
                        background: `linear-gradient(90deg, ${lang.accent}00, ${lang.accent}, ${lang.accent}00)`,
                        opacity: isHovered ? 1 : 0.3
                      }}
                    />
                    <span
                      className="inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300"
                      style={{ color: lang.accent }}
                      data-testid={`language-cta-${lang.code}`}
                    >
                      {lang.cta}
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      >
                        <path d="M5 12h14" />
                        <path d="M13 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>

                  <div
                    className="absolute left-0 right-0 bottom-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                    style={{
                      background: `linear-gradient(90deg, ${lang.accent}00, ${lang.accent}, ${lang.accent}00)`,
                      boxShadow: `0 0 18px ${lang.accent}`
                    }}
                  />
                </button>
              );
            })}
          </div>

          <p className="mt-10 text-center text-xs tracking-[0.25em] uppercase text-[#556475]">
            You can switch anytime from the header.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-6 border-t border-white/5 flex items-center justify-between text-[11px] tracking-[0.25em] uppercase text-[#556475]">
        <span>© The DCA Club · Educational tool</span>
        <div className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: '#22c55e', boxShadow: '0 0 10px #22c55e' }}
          />
          Live
        </div>
      </footer>
    </div>
  );
};

export default LanguageSelect;
